"use client";

import { ReactNode } from "react";
import { useTranslations } from "next-intl";

interface TeamCardStatsProps {
  wins: number;
  losses: number;
  achievements?: ReactNode;
}

export function TeamCardStats({
  wins,
  losses,
  achievements,
}: TeamCardStatsProps) {
  const t = useTranslations();

  const total = wins + losses;
  const winRate = total === 0 ? 0 : Math.round((wins / total) * 100);

  // Determine win rate color class using HeroUI semantic colors
  const getWinRateClass = () => {
    if (total === 0) return "text-default-500";
    if (winRate >= 75) return "text-success-400";
    if (winRate >= 50) return "text-warning-400";
    return "text-danger-400";
  };

  return (
    <div className="mt-1 sm:mt-2.5 md:mt-3 flex flex-col items-center justify-center gap-1.5 rounded sm:rounded-lg md:rounded-xl bg-black/20 px-1 py-1 sm:px-2 sm:py-2.5 md:py-3">
      {/* Compact W-L display on mobile, full labels on larger screens */}
      <div className="flex items-center gap-1 sm:hidden">
        <span className="font-cinzel text-[0.8rem] font-bold text-success-400">
          {wins}
        </span>
        <span className="text-[0.7rem] text-default-500">-</span>
        <span className="font-cinzel text-[0.8rem] font-bold text-danger-400">
          {losses}
        </span>
        <span className="text-[0.65rem] text-default-500 ml-0.5">|</span>
        <span
          className={`font-cinzel text-[0.8rem] font-bold ${getWinRateClass()}`}
        >
          {winRate}%
        </span>
      </div>

      {/* Full grid on larger screens */}
      <div className="hidden sm:grid grid-cols-3 gap-1 text-center w-full items-start">
        <div>
          <div className="mb-0.5 text-[0.6rem] md:text-xs uppercase tracking-wider text-default-500">
            {t("tower.stats.wins")}
          </div>
          <div className="font-cinzel text-[0.85rem] md:text-[1.05rem] font-bold">
            {wins}
          </div>
        </div>

        <div>
          <div className="mb-0.5 text-[0.6rem] md:text-xs uppercase tracking-wider text-default-500">
            {t("tower.stats.losses")}
          </div>
          <div className="font-cinzel text-[0.85rem] md:text-[1.05rem] font-bold">
            {losses}
          </div>
        </div>

        <div>
          <div className="mb-0.5 text-[0.6rem] md:text-xs uppercase tracking-wider text-default-500">
            {t("tower.stats.winRate")}
          </div>
          <div
            className={`font-cinzel text-[0.85rem] md:text-[1.05rem] font-bold ${getWinRateClass()}`}
          >
            {winRate}%
          </div>
        </div>
      </div>

      {achievements && (
        <div className="mt-0.5 w-full border-t border-white/5 pt-1.5">
          {achievements}
        </div>
      )}
    </div>
  );
}
